import type { SortingState } from '@tanstack/react-table'

import { normalizeBillBuyPartyType } from '@/constants/bill-buy-party'
import { BILL_LOCATION_LABELS, type BillLocationValue } from '@/constants/bill-location'
import { BILL_SALE_MODE_LABELS_TH } from '@/constants/bill-sale-mode'
import { DlInstance } from '@/lib/axios'
import dayjs from '@/lib/dayjs'
import type { Bill } from '@/models/bill/bill'
import { datatableSortingStateToQuery } from '@/utils/api'

/** ดึงต่อครั้ง — ลดจำนวนรอบเรียก API */
const EXPORT_PAGE_SIZE = 500
/** กันวนลูปเกิน (ข้อมูลจริงยัง export ได้ถ้าต่ำกว่าเพดานนี้) */
const MAX_EXPORT_PAGES = 400

export type BillExportTemplate =
  | 'buy_monthly'
  | 'sell_retail_monthly'
  | 'sell_retail_customer_monthly'
  | 'sell_wholesale_all_monthly'
  | 'sell_wholesale_monthly'
  | 'sell_all_monthly'

export type FetchBillsExportParams = {
  search: string
  status: string
  sort: SortingState
  sale_mode?: string
  bill_date_from?: string
  bill_date_to?: string
  location?: string
  payment_method?: string
  payment_status?: string
  outstanding?: boolean
  print_status?: string
}

export type CustomerExportOption = {
  id: string
  name: string
  phone_number: string
  car_registration: string
}

export type BuildExportCsvOptions = {
  template: BillExportTemplate
  catalog: ExportCatalog
  customerId?: string
  customers?: CustomerExportOption[]
}

/** ข้อมูลอ้างอิงจาก picklist (เกรด/ประเภททุเรียน) และตารางขนาด */
export type ExportCatalog = {
  gradeLabels: string[]
  durianTypeLabels: string[]
  sizeNameById: Map<string, string>
  gradeIndex: Map<string, string>
  durianTypeIndex: Map<string, string>
  sizeNameIndex: Map<string, string>
}

type BillsListApiBody = {
  data: Bill[]
  meta: { total: number; currentPage: number; totalPages: number }
}

type LineEntry = {
  durian_type: string
  grade: string
  size: string
  description: string
  qty: number
  price: number
  amount: number
}

type GradeSlot = { qty: number; price: number; total: number }

type PicklistApiRow = { label: string; sort_order?: number }
type SizeApiRow = { id: string; name: string }

function csvEscape(value: string | number | null | undefined): string {
  const s = value === null || value === undefined ? '' : String(value)
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`
  }
  return s
}

function csvLine(cells: (string | number)[]): string {
  return cells.map((c) => csvEscape(c)).join(',')
}

function emptySlot(): GradeSlot {
  return { qty: 0, price: 0, total: 0 }
}

function normalizeText(s: string): string {
  return s.trim().toLowerCase().replace(/\s+/g, ' ')
}

function buildLabelIndex(labels: string[]): Map<string, string> {
  const index = new Map<string, string>()
  for (const label of labels) {
    const key = normalizeText(label)
    if (key) index.set(key, label)
  }
  return index
}

function resolveCatalogLabel(raw: string | null | undefined, index: Map<string, string>): string | null {
  const trimmed = (raw ?? '').trim()
  if (!trimmed) return null
  return index.get(normalizeText(trimmed)) ?? null
}

export async function fetchExportCatalog(): Promise<ExportCatalog> {
  const [gradeRes, durianRes, sizeRes] = await Promise.all([
    DlInstance.get<{ data: PicklistApiRow[] }>('/picklists', { params: { kind: 'grade' } }),
    DlInstance.get<{ data: PicklistApiRow[] }>('/picklists', { params: { kind: 'durian_type' } }),
    DlInstance.get<{ data: SizeApiRow[] }>('/sizes/no-page', { params: { status: 'active' } }),
  ])

  const sortLabels = (rows: PicklistApiRow[]) =>
    [...rows]
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
      .map((r) => r.label.trim())
      .filter(Boolean)

  const gradeLabels = sortLabels(gradeRes.data?.data ?? [])
  const durianTypeLabels = sortLabels(durianRes.data?.data ?? [])

  const sizeNameById = new Map<string, string>()
  const sizeNames: string[] = []
  for (const s of sizeRes.data?.data ?? []) {
    const name = s.name?.trim()
    if (!name) continue
    sizeNameById.set(s.id, name)
    sizeNames.push(name)
  }

  return {
    gradeLabels,
    durianTypeLabels,
    sizeNameById,
    gradeIndex: buildLabelIndex(gradeLabels),
    durianTypeIndex: buildLabelIndex(durianTypeLabels),
    sizeNameIndex: buildLabelIndex(sizeNames),
  }
}

function catalogSizeName(catalog: ExportCatalog, sizeRaw: string | null | undefined): string {
  const raw = (sizeRaw ?? '').trim()
  if (!raw) return ''
  return catalog.sizeNameById.get(raw) ?? resolveCatalogLabel(raw, catalog.sizeNameIndex) ?? raw
}

function resolveLineGradeColumn(line: LineEntry, catalog: ExportCatalog): string | null {
  const candidates = [
    line.grade,
    catalogSizeName(catalog, line.size),
    line.description,
  ]
  for (const raw of candidates) {
    const fromGrade = resolveCatalogLabel(raw, catalog.gradeIndex)
    if (fromGrade) return fromGrade
    const fromSize = resolveCatalogLabel(raw, catalog.sizeNameIndex)
    if (fromSize && catalog.gradeIndex.has(normalizeText(fromSize))) {
      return catalog.gradeIndex.get(normalizeText(fromSize))!
    }
  }
  const trimmedGrade = line.grade.trim()
  return trimmedGrade || null
}

function resolveLineDurianLabel(line: LineEntry, catalog: ExportCatalog): string {
  return (
    resolveCatalogLabel(line.durian_type, catalog.durianTypeIndex) ??
    line.durian_type.trim() ??
    ''
  )
}

function formatRetailItemLabel(line: LineEntry, catalog: ExportCatalog): string {
  const durian = resolveLineDurianLabel(line, catalog)
  const grade = resolveLineGradeColumn(line, catalog) ?? line.grade.trim()
  const parts = [durian, grade].filter(Boolean)
  if (parts.length) return parts.join(' ')
  return line.description || '-'
}

function formatBillDate(billDate: string | undefined): string {
  if (!billDate) return ''
  const d = dayjs(billDate)
  return d.isValid() ? d.format('YYYY-MM-DD') : billDate
}

function formatBillLocation(b: Bill): string {
  if (!b.location) return ''
  return BILL_LOCATION_LABELS[b.location as BillLocationValue] ?? b.location
}

function getBillTotalAmount(b: Bill): number {
  if (typeof b.total_amount === 'number' && !Number.isNaN(b.total_amount)) {
    return b.total_amount
  }
  const fromLines = getBillLineEntries(b).reduce((acc, li) => acc + li.amount, 0)
  if (fromLines > 0) return fromLines
  return (
    b.order_information?.reduce(
      (acc, o) =>
        acc +
        o.cycles.reduce((a, c) => a + (Number(c.product_quantity) || 0), 0) *
          (Number(o.product_price) || 0),
      0,
    ) ?? 0
  )
}

function lineQtyFromItem(li: {
  weight_kg?: number
  box_count?: number | null
  weight_per_box_kg?: number | null
  line_total?: number
  price_per_kg?: number
}): number {
  const weight = Number(li.weight_kg) || 0
  if (weight > 0) return weight
  const boxes = Number(li.box_count) || 0
  const perBox = Number(li.weight_per_box_kg) || 0
  if (boxes > 0 && perBox > 0) return boxes * perBox
  return 0
}

function mapLineItemToEntry(li: NonNullable<Bill['line_items']>[number]): LineEntry {
  const qty = lineQtyFromItem(li)
  const price = Number(li.price_per_kg) || 0
  const amount =
    typeof li.line_total === 'number' && !Number.isNaN(li.line_total) ? li.line_total : qty * price
  const description =
    li.description?.trim() ||
    [li.durian_type, li.grade, li.size].filter(Boolean).join(' ').trim()
  return {
    durian_type: li.durian_type ?? '',
    grade: li.grade ?? '',
    size: li.size ?? '',
    description,
    qty,
    price,
    amount,
  }
}

function mapOrderToEntry(order: NonNullable<Bill['order_information']>[number]): LineEntry {
  const qty =
    order.cycles?.reduce((a, c) => a + (Number(c.product_quantity) || 0), 0) ?? 0
  const price = Number(order.product_price) || 0
  return {
    durian_type: order.durian_type ?? '',
    grade: order.grade ?? '',
    size: order.size ?? '',
    description:
      order.description?.trim() ||
      [order.durian_type, order.grade, order.size].filter(Boolean).join(' ').trim(),
    qty,
    price,
    amount: qty * price,
  }
}

export function getBillLineEntries(b: Bill): LineEntry[] {
  const fromLineItems = (b.line_items ?? []).map(mapLineItemToEntry)
  const fromOrders = (b.order_information ?? []).map(mapOrderToEntry)

  const hasQty = (entries: LineEntry[]) => entries.some((e) => e.qty > 0 || e.amount > 0)

  if (fromLineItems.length > 0 && hasQty(fromLineItems)) return fromLineItems
  if (fromOrders.length > 0 && hasQty(fromOrders)) return fromOrders
  if (fromLineItems.length > 0) return fromLineItems
  return fromOrders
}

function collectGradeColumns(lines: LineEntry[], catalog: ExportCatalog): string[] {
  const columns = [...catalog.gradeLabels]
  const seen = new Set(columns.map(normalizeText))

  for (const line of lines) {
    const col = resolveLineGradeColumn(line, catalog)
    if (!col) continue
    const key = normalizeText(col)
    if (!seen.has(key)) {
      seen.add(key)
      columns.push(col)
    }
  }
  return columns
}

function buildGradeMatrixByLabels(
  lines: LineEntry[],
  gradeColumns: string[],
  catalog: ExportCatalog,
): Map<string, GradeSlot> {
  const matrix = new Map<string, GradeSlot>()
  for (const label of gradeColumns) {
    matrix.set(label, emptySlot())
  }

  for (const line of lines) {
    if (line.qty <= 0 && line.amount <= 0) continue
    const col = resolveLineGradeColumn(line, catalog)
    if (!col || !matrix.has(col)) continue

    const slot = matrix.get(col)!
    slot.qty += line.qty
    slot.total += line.amount
    if (line.qty > 0) slot.price = line.price
  }

  for (const slot of matrix.values()) {
    if (slot.qty > 0 && slot.total > 0) {
      slot.price = slot.total / slot.qty
    }
  }

  return matrix
}

function gradeMatrixToRowCells(matrix: Map<string, GradeSlot>, gradeColumns: string[]): string[] {
  const cells: string[] = []
  let rowTotal = 0
  for (const label of gradeColumns) {
    const slot = matrix.get(label) ?? emptySlot()
    cells.push(String(slot.qty), String(slot.price), String(slot.total))
    rowTotal += slot.total
  }
  cells.push(String(rowTotal))
  return cells
}

/** แถวรวม: รวมจำนวน/ยอดต่อเกรด — คอลัมน์ราคาเว้นว่าง (ไม่แสดงราคาเฉลี่ย) */
function gradeMatrixToSummaryCells(matrix: Map<string, GradeSlot>, gradeColumns: string[]): string[] {
  const cells: string[] = []
  let rowTotal = 0
  for (const label of gradeColumns) {
    const slot = matrix.get(label) ?? emptySlot()
    cells.push(slot.qty > 0 ? String(slot.qty) : '', '', slot.total > 0 ? String(slot.total) : '')
    rowTotal += slot.total
  }
  cells.push(rowTotal > 0 ? String(rowTotal) : '')
  return cells
}

function buildGradeMatrixHeaders(gradeColumns: string[]): string[] {
  const headers: string[] = []
  for (const label of gradeColumns) {
    headers.push(label, 'ราคา', 'รวม')
  }
  headers.push('ยอดรวม')
  return headers
}

const SUMMARY_ROW_LABEL = 'รวม'

function buildLeadingSummaryCells(label: string, columnCount: number): string[] {
  if (columnCount <= 0) return [label]
  return [label, ...Array.from({ length: columnCount - 1 }, () => '')]
}

function aggregateGradeMatrixFromBills(
  bills: Bill[],
  gradeColumns: string[],
  catalog: ExportCatalog,
): Map<string, GradeSlot> {
  const allLines = bills.flatMap((b) => getBillLineEntries(b))
  return buildGradeMatrixByLabels(allLines, gradeColumns, catalog)
}

function buildGradeMatrixSummaryRow(
  gradeColumns: string[],
  matrix: Map<string, GradeSlot>,
  leadingColumnCount: number,
  label = SUMMARY_ROW_LABEL,
): string[] {
  return [
    ...buildLeadingSummaryCells(label, leadingColumnCount),
    ...gradeMatrixToSummaryCells(matrix, gradeColumns),
  ]
}

function sumColumnFromRows(rows: string[][], columnIndex: number): number {
  return rows.reduce((sum, row) => sum + (Number(row[columnIndex]) || 0), 0)
}

function sortBillsByDate(rows: Bill[]): Bill[] {
  return [...rows].sort((a, b) => {
    const da = a.bill_date ?? ''
    const db = b.bill_date ?? ''
    if (da !== db) return da.localeCompare(db)
    return (a.bill_number ?? '').localeCompare(b.bill_number ?? '')
  })
}

export function filterBillsByWholesaleCustomer(
  rows: Bill[],
  customerId: string | undefined,
  customers: CustomerExportOption[] | undefined,
): Bill[] {
  if (!customerId || customerId === 'all' || !customers?.length) return rows

  const selected = customers.find((c) => c.id === customerId)
  if (!selected) return rows

  const norm = (s: string) => normalizeText(s)
  const targetName = norm(selected.name)
  const targetPhone = norm(selected.phone_number ?? '')
  const targetCar = norm(selected.car_registration ?? '')

  return rows.filter((b) => {
    const name = norm(b.customer_information?.name ?? '')
    const phone = norm(b.customer_information?.phone ?? '')
    const car = norm(b.customer_information?.car_registration ?? '')
    if (targetName && name === targetName) return true
    if (targetPhone && phone === targetPhone) return true
    if (targetCar && car === targetCar) return true
    return false
  })
}

const RETAIL_MONTHLY_HEADERS = [
  'วันที่',
  'สถานที่',
  'รายการ',
  'จำนวน',
  'ราคา',
  'ยอด',
  'หมายเหตุ',
] as const

const WHOLESALE_LEDGER_HEADERS = [
  'วันที่',
  'สถานที่',
  'เลขบิล',
  'ยอดขาย',
  'วันที่โอน',
  'ยอดโอน',
  'คงเหลือ',
] as const

function buyPartyExportColumns(b: Bill): { stall: string; orchard: string } {
  const name = b.customer_information?.name ?? ''
  const partyType = normalizeBillBuyPartyType(b.bill_information?.buy_party_type)
  if (partyType === 'stall') {
    return { stall: name, orchard: '' }
  }
  return { stall: '', orchard: name }
}

function buyBillToRow(b: Bill, gradeColumns: string[], catalog: ExportCatalog): string[] {
  const lines = getBillLineEntries(b)
  const matrix = buildGradeMatrixByLabels(lines, gradeColumns, catalog)
  const { stall, orchard } = buyPartyExportColumns(b)

  return [
    formatBillDate(b.bill_date),
    formatBillLocation(b),
    stall,
    orchard,
    ...gradeMatrixToRowCells(matrix, gradeColumns),
  ]
}

function buildBuyMonthlyCsv(rows: Bill[], catalog: ExportCatalog): string {
  const sorted = sortBillsByDate(rows)
  const allLines = sorted.flatMap((b) => getBillLineEntries(b))
  const gradeColumns = collectGradeColumns(allLines, catalog)
  const headers = ['เดือน', 'สถานที่', 'หน้าแผง', 'ชื่อสวน', ...buildGradeMatrixHeaders(gradeColumns)]
  const body = sorted.map((b) => csvLine(buyBillToRow(b, gradeColumns, catalog)))
  const summaryMatrix = aggregateGradeMatrixFromBills(sorted, gradeColumns, catalog)
  const summaryRow = csvLine(buildGradeMatrixSummaryRow(gradeColumns, summaryMatrix, 4))
  return [csvLine(headers), ...body, '', summaryRow].join('\r\n')
}

function buildSellRetailMonthlyCsv(rows: Bill[], catalog: ExportCatalog): string {
  const sorted = sortBillsByDate(rows)
  const dataRows: string[][] = []

  for (const b of sorted) {
    const billDate = formatBillDate(b.bill_date)
    for (const li of getBillLineEntries(b)) {
      if (li.qty <= 0 && li.amount <= 0) continue
      dataRows.push([
        billDate,
        formatBillLocation(b),
        formatRetailItemLabel(li, catalog),
        String(li.qty),
        String(li.price),
        String(li.amount),
        '',
      ])
    }
  }

  const totalQty = sumColumnFromRows(dataRows, 3)
  const totalAmount = sumColumnFromRows(dataRows, 5)
  const summaryRow = csvLine([
    SUMMARY_ROW_LABEL,
    '',
    '',
    String(totalQty),
    '',
    String(totalAmount),
    '',
  ])

  const lines = [
    csvLine([...RETAIL_MONTHLY_HEADERS]),
    ...dataRows.map((r) => csvLine(r)),
    '',
    summaryRow,
  ]
  return lines.join('\r\n')
}

function wholesaleBillDetailToRow(
  b: Bill,
  gradeColumns: string[],
  catalog: ExportCatalog,
): string[] {
  const lines = getBillLineEntries(b)
  const matrix = buildGradeMatrixByLabels(lines, gradeColumns, catalog)

  return [
    formatBillDate(b.bill_date),
    formatBillLocation(b),
    b.customer_information?.name ?? '',
    b.bill_number ?? '',
    ...gradeMatrixToRowCells(matrix, gradeColumns),
  ]
}

function wholesaleLedgerRow(b: Bill): string[] {
  const totalSale = getBillTotalAmount(b)
  const paid =
    typeof b.payment_information?.total_paid === 'number'
      ? b.payment_information.total_paid
      : typeof b.paid_amount === 'number'
        ? b.paid_amount
        : 0
  const remaining =
    typeof b.payment_information?.remaining_amount === 'number'
      ? b.payment_information.remaining_amount
      : typeof b.remaining_amount === 'number'
        ? b.remaining_amount
        : Math.max(0, totalSale - paid)

  const transferRounds =
    b.payment_rounds?.filter((r) => r.payment_method === 'transfer') ?? []
  const lastTransfer = transferRounds.length
    ? transferRounds.reduce((latest, r) =>
        !latest || (r.paid_at && r.paid_at > latest.paid_at) ? r : latest,
      )
    : null

  const transferDate =
    lastTransfer?.paid_at != null
      ? formatBillDate(lastTransfer.paid_at.slice(0, 10))
      : b.payment_information?.payment_method === 'transfer'
        ? formatBillDate(b.bill_date)
        : ''

  const transferAmount =
    transferRounds.length > 0
      ? transferRounds.reduce((s, r) => s + (Number(r.paid_amount) || 0), 0)
      : b.payment_information?.payment_method === 'transfer'
        ? paid
        : 0

  return [
    formatBillDate(b.bill_date),
    formatBillLocation(b),
    b.bill_number ?? '',
    String(totalSale),
    transferDate,
    String(transferAmount),
    String(remaining),
  ]
}

function buildSellWholesaleMonthlyCsv(rows: Bill[], catalog: ExportCatalog): string {
  const sorted = sortBillsByDate(rows)
  const allLines = sorted.flatMap((b) => getBillLineEntries(b))
  const gradeColumns = collectGradeColumns(allLines, catalog)
  const detailHeaders = [
    'วันที่',
    'สถานที่',
    'ชื่อ',
    'เลขที่บิล',
    ...buildGradeMatrixHeaders(gradeColumns),
  ]

  const ledgerDataRows = sorted.map((b) => wholesaleLedgerRow(b))
  const ledgerSummary = csvLine([
    SUMMARY_ROW_LABEL,
    '',
    '',
    String(sumColumnFromRows(ledgerDataRows, 3)),
    '',
    String(sumColumnFromRows(ledgerDataRows, 5)),
    String(sumColumnFromRows(ledgerDataRows, 6)),
  ])

  const summaryMatrix = aggregateGradeMatrixFromBills(sorted, gradeColumns, catalog)
  const detailSummary = csvLine(buildGradeMatrixSummaryRow(gradeColumns, summaryMatrix, 4))

  const ledgerLines = [
    csvLine([...WHOLESALE_LEDGER_HEADERS]),
    ...ledgerDataRows.map((r) => csvLine(r)),
    '',
    ledgerSummary,
    '',
    csvLine(detailHeaders),
    ...sorted.map((b) => csvLine(wholesaleBillDetailToRow(b, gradeColumns, catalog))),
    '',
    detailSummary,
  ]
  return ledgerLines.join('\r\n')
}

function partitionSellExportRows(rows: Bill[]): { retail: Bill[]; wholesale: Bill[] } {
  const retail: Bill[] = []
  const wholesale: Bill[] = []
  for (const b of rows) {
    if ((b.bill_information?.sale_mode ?? 'retail') === 'wholesale') {
      wholesale.push(b)
    } else {
      retail.push(b)
    }
  }
  return { retail, wholesale }
}

function buildSellAllMonthlyCsv(rows: Bill[], catalog: ExportCatalog): string {
  const { retail, wholesale } = partitionSellExportRows(rows)
  const sections: string[] = []

  if (hasExportableRows(retail, 'sell_retail_monthly')) {
    sections.push(csvLine(['ขายปลีก']), buildSellRetailMonthlyCsv(retail, catalog))
  }

  if (hasExportableRows(wholesale, 'sell_wholesale_monthly')) {
    if (sections.length > 0) sections.push('')
    sections.push(csvLine(['ขายส่ง']), buildSellWholesaleMonthlyCsv(wholesale, catalog))
  }

  return sections.join('\r\n')
}

export function hasExportableRows(rows: Bill[], template: BillExportTemplate): boolean {
  if (template === 'sell_all_monthly') {
    const { retail, wholesale } = partitionSellExportRows(rows)
    return (
      hasExportableRows(retail, 'sell_retail_monthly') ||
      hasExportableRows(wholesale, 'sell_wholesale_monthly')
    )
  }
  if (template === 'sell_retail_monthly') {
    return rows.some((b) =>
      getBillLineEntries(b).some((li) => li.qty > 0 || li.amount > 0),
    )
  }
  if (
    template === 'buy_monthly' ||
    template === 'sell_wholesale_monthly' ||
    template === 'sell_wholesale_all_monthly' ||
    template === 'sell_retail_customer_monthly'
  ) {
    return rows.some((b) =>
      getBillLineEntries(b).some((li) => li.qty > 0 || li.amount > 0),
    )
  }
  return rows.length > 0
}

export function filterRowsForExportTemplate(
  rows: Bill[],
  options: Pick<BuildExportCsvOptions, 'template' | 'customerId' | 'customers'>,
): Bill[] {
  const { template, customerId, customers } = options
  let filtered = rows

  if (template === 'sell_retail_customer_monthly') {
    filtered = filterBillsByWholesaleCustomer(filtered, customerId, customers)
    filtered = filtered.filter(
      (b) => (b.bill_information?.sale_mode ?? 'retail') !== 'wholesale',
    )
  }

  if (template === 'sell_wholesale_monthly') {
    filtered = filterBillsByWholesaleCustomer(filtered, customerId, customers)
    filtered = filtered.filter(
      (b) => (b.bill_information?.sale_mode ?? 'retail') === 'wholesale',
    )
  }

  if (template === 'sell_wholesale_all_monthly') {
    filtered = filtered.filter(
      (b) => (b.bill_information?.sale_mode ?? 'retail') === 'wholesale',
    )
  }

  if (template === 'sell_retail_monthly') {
    filtered = filtered.filter(
      (b) => (b.bill_information?.sale_mode ?? 'retail') !== 'wholesale',
    )
  }

  if (template === 'sell_all_monthly') {
    return filtered
  }

  return filtered
}

export function buildExportCsvContent(
  rows: Bill[],
  options: Pick<BuildExportCsvOptions, 'template' | 'catalog'>,
): string {
  const { template, catalog } = options

  switch (template) {
    case 'buy_monthly':
      return buildBuyMonthlyCsv(rows, catalog)
    case 'sell_retail_monthly':
    case 'sell_retail_customer_monthly':
      return buildSellRetailMonthlyCsv(rows, catalog)
    case 'sell_wholesale_monthly':
    case 'sell_wholesale_all_monthly':
      return buildSellWholesaleMonthlyCsv(rows, catalog)
    case 'sell_all_monthly':
      return buildSellAllMonthlyCsv(rows, catalog)
    default:
      return buildBuyMonthlyCsv(rows, catalog)
  }
}

export type ExportTemplateValidationKey =
  | 'export_template_buy_requires_buy_tab'
  | 'export_template_sell_requires_sell_tab'
  | 'export_template_retail_customer_required'
  | 'export_template_wholesale_customer_required'

export function validateExportTemplate(
  template: BillExportTemplate,
  tab: string,
  customerId?: string,
): ExportTemplateValidationKey | null {
  if (template === 'buy_monthly' && tab !== 'buy') {
    return 'export_template_buy_requires_buy_tab'
  }
  if (
    (template === 'sell_retail_monthly' ||
      template === 'sell_retail_customer_monthly' ||
      template === 'sell_wholesale_all_monthly' ||
      template === 'sell_wholesale_monthly' ||
      template === 'sell_all_monthly') &&
    tab !== 'sell'
  ) {
    return 'export_template_sell_requires_sell_tab'
  }
  if (template === 'sell_retail_customer_monthly' && !customerId) {
    return 'export_template_retail_customer_required'
  }
  if (template === 'sell_wholesale_monthly' && !customerId) {
    return 'export_template_wholesale_customer_required'
  }
  return null
}

export function saleModeForExportTemplate(template: BillExportTemplate): string | undefined {
  if (template === 'sell_retail_monthly') return 'retail'
  if (template === 'sell_retail_customer_monthly') return 'retail'
  if (template === 'sell_wholesale_all_monthly') return 'wholesale'
  if (template === 'sell_wholesale_monthly') return 'wholesale'
  return undefined
}

export function defaultExportTemplateForTab(tab: string): BillExportTemplate {
  if (tab === 'buy') return 'buy_monthly'
  return 'sell_retail_monthly'
}

export function exportFilenameForTemplate(
  template: BillExportTemplate,
  stamp: string,
  outstanding?: boolean,
): string {
  const prefixMap: Record<BillExportTemplate, string> = {
    buy_monthly: 'buy-monthly',
    sell_retail_monthly: 'sell-retail-monthly',
    sell_retail_customer_monthly: 'sell-retail-customer-monthly',
    sell_wholesale_all_monthly: 'sell-wholesale-all-monthly',
    sell_wholesale_monthly: 'sell-wholesale-monthly',
    sell_all_monthly: 'sell-all-monthly',
  }
  const base = outstanding ? 'bills-outstanding' : prefixMap[template]
  return `${base}-${stamp}.csv`
}

/** @deprecated Use buildExportCsvContent with template instead */
export const BILL_EXPORT_HEADERS_TH = [
  'ลำดับ',
  'รหัสบิล',
  'เลขที่ใบเสร็จ',
  'วันที่บิล',
  'สถานที่',
  'ชื่อลูกค้า',
  'เบอร์โทร',
  'ทะเบียนรถ',
  'ประเภทใบเสร็จ',
  'ช่องทางขาย',
  'จำนวนรวม(กก.)',
  'ราคารวม(บาท)',
  'วิธีชำระ',
  'สถานะชำระ',
  'สถานะพิมพ์',
  'วันที่สร้าง',
  'วันที่อัปเดต',
  'สร้างโดย',
  'อัปเดตโดย',
] as const

function billToCsvRow(b: Bill, index: number): string[] {
  const totalQty =
    b.order_information?.reduce(
      (acc, o) => acc + o.cycles.reduce((a, c) => a + (Number(c.product_quantity) || 0), 0),
      0,
    ) ?? 0
  const totalPrice = getBillTotalAmount(b)
  const loc = b.location
    ? (BILL_LOCATION_LABELS[b.location as BillLocationValue] ?? b.location)
    : ''
  const billType = b.bill_information?.bill_type === 'sell' ? 'ขาย' : 'ซื้อ'
  const saleModeRaw = b.bill_information?.sale_mode
  const saleModeLabel =
    b.bill_information?.bill_type === 'sell'
      ? BILL_SALE_MODE_LABELS_TH[saleModeRaw === 'wholesale' ? 'wholesale' : 'retail']
      : '-'
  const payMethod = b.payment_information?.payment_method === 'cash' ? 'เงินสด' : 'โอน'
  const ps = b.payment_information?.payment_status
  const payStatus =
    ps === 'paid' ? 'ชำระแล้ว' : ps === 'partial' ? 'ชำระบางส่วน' : 'ยังไม่ชำระ'
  const printSt = b.print_status === 'printed' ? 'พิมพ์แล้ว' : 'ยังไม่พิมพ์'

  return [
    String(index + 1),
    b.id ?? '',
    b.bill_number ?? '',
    b.bill_date ?? '',
    loc,
    b.customer_information?.name ?? '',
    b.customer_information?.phone ?? '',
    b.customer_information?.car_registration ?? '',
    billType,
    saleModeLabel,
    String(totalQty),
    String(totalPrice),
    payMethod,
    payStatus,
    printSt,
    b.created_at ?? '',
    b.updated_at ?? '',
    b.created_by?.name ?? '',
    b.updated_by?.name ?? '',
  ]
}

/** @deprecated Use buildExportCsvContent */
export function buildBillsCsvContent(rows: Bill[]): string {
  const headerLine = BILL_EXPORT_HEADERS_TH.map((h) => csvEscape(h)).join(',')
  const bodyLines = rows.map((b, i) => billToCsvRow(b, i).map(csvEscape).join(','))
  return [headerLine, ...bodyLines].join('\r\n')
}

export function downloadCsvFile(filename: string, csvBody: string): void {
  const blob = new Blob([`\uFEFF${csvBody}`], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.rel = 'noopener'
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

export async function fetchAllBillsForExport(params: FetchBillsExportParams): Promise<{
  rows: Bill[]
  truncated: boolean
}> {
  const baseParams = {
    pageSize: EXPORT_PAGE_SIZE,
    search: params.search || undefined,
    bill_type: params.status,
    sale_mode: params.sale_mode,
    bill_date_from: params.bill_date_from,
    bill_date_to: params.bill_date_to,
    location: params.location,
    print_status: params.print_status,
    payment_method: params.payment_method,
    payment_status: params.payment_status,
    outstanding: params.outstanding ? 'true' : undefined,
    sort: datatableSortingStateToQuery(params.sort),
  }

  const first = await DlInstance.get<BillsListApiBody>('/bills', {
    params: { page: 1, ...baseParams },
  })

  const body = first.data
  if (!body?.data || !Array.isArray(body.data)) {
    return { rows: [], truncated: false }
  }

  const totalPages = Math.min(body.meta?.totalPages ?? 1, MAX_EXPORT_PAGES)
  const truncated = (body.meta?.totalPages ?? 1) > MAX_EXPORT_PAGES

  let all: Bill[] = [...body.data]
  for (let p = 2; p <= totalPages; p++) {
    const res = await DlInstance.get<BillsListApiBody>('/bills', {
      params: { page: p, ...baseParams },
    })
    if (res.data?.data?.length) {
      all = all.concat(res.data.data)
    }
  }

  return { rows: all, truncated }
}
